import { useCallback, useRef, useState } from "react";

const STUB_EXT = /\.(pdf|csv)$/i;

/** Walk one dropped entry (file or folder) and collect every PDF/CSV in it. */
const walkEntry = (entry) => new Promise((resolve) => {
  if (!entry) return resolve([]);
  if (entry.isFile) {
    entry.file((f) => resolve(STUB_EXT.test(f.name) ? [f] : []), () => resolve([]));
    return;
  }
  if (!entry.isDirectory) return resolve([]);
  const reader = entry.createReader();
  const out = [];
  const readBatch = () => reader.readEntries(async (entries) => {
    if (!entries.length) return resolve(out);
    for (const e of entries) out.push(...(await walkEntry(e)));
    readBatch(); // readEntries returns in chunks of ~100 — keep going until empty
  }, () => resolve(out));
  readBatch();
});

/**
 * Every pay-stub file (PDF or CSV) in a drop — whole folders included,
 * recursively. Falls back to the flat file list when the browser can't
 * expose directory entries.
 */
export const collectStubFiles = async (dataTransfer) => {
  const items = Array.from(dataTransfer?.items || []);
  const entries = items.map((it) => (it.webkitGetAsEntry ? it.webkitGetAsEntry() : null));
  if (entries.some(Boolean)) {
    const nested = await Promise.all(entries.map(walkEntry));
    return nested.flat();
  }
  return Array.from(dataTransfer?.files || []).filter((f) => STUB_EXT.test(f.name));
};

/**
 * Drag-and-drop plumbing for a stub drop zone. `onFiles(files)` receives
 * the flattened PDF/CSV list; `dragging` drives the overlay.
 */
export const useStubDrop = (onFiles, { disabled = false } = {}) => {
  const [dragging, setDragging] = useState(false);
  const depth = useRef(0); // dragenter/leave fire for every child — count them

  const onDragEnter = useCallback((e) => {
    if (disabled || !Array.from(e.dataTransfer?.types || []).includes("Files")) return;
    e.preventDefault();
    depth.current += 1;
    setDragging(true);
  }, [disabled]);

  const onDragOver = useCallback((e) => {
    if (disabled || !Array.from(e.dataTransfer?.types || []).includes("Files")) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  }, [disabled]);

  const onDragLeave = useCallback((e) => {
    if (depth.current === 0) return;
    e.preventDefault();
    depth.current -= 1;
    if (depth.current <= 0) { depth.current = 0; setDragging(false); }
  }, []);

  const onDrop = useCallback(async (e) => {
    if (!Array.from(e.dataTransfer?.types || []).includes("Files")) return;
    e.preventDefault();
    depth.current = 0;
    setDragging(false);
    if (disabled) return;
    const files = await collectStubFiles(e.dataTransfer);
    onFiles(files);
  }, [disabled, onFiles]);

  return { dragging, dropProps: { onDragEnter, onDragOver, onDragLeave, onDrop } };
};

/** Full-surface overlay shown while files hover over a drop zone. */
export const StubDropOverlay = ({ show, testId = "stub-drop-overlay" }) => (
  show ? (
    <div data-testid={testId}
      className="pointer-events-none absolute inset-0 z-[60] flex items-center justify-center rounded-xl border-2 border-dashed border-[#1a3fd1] bg-[#1a3fd1]/10 backdrop-blur-[2px]">
      <div className="rounded-lg bg-white px-5 py-3 text-center shadow-xl dark:bg-[#0d0d0d]">
        <p className="text-sm font-bold uppercase tracking-widest text-gray-900 dark:text-white">Drop pay stubs to import</p>
        <p className="mt-1 font-mono text-[10px] text-gray-600 dark:text-white/60">PDF or CSV · folders are scanned too · duplicates are skipped</p>
      </div>
    </div>
  ) : null
);

const BATCH = 5;

/**
 * Send stubs to the bulk importer in small batches so the counter moves as
 * files are scanned (one giant request would sit at 100% uploaded while the
 * server parsed everything). `onProgress({done, total, pct, current})` fires
 * on every upload tick; the merged result matches the API's single-call shape.
 */
export const importStubsInBatches = async (api, files, onProgress) => {
  const total = files.length;
  const merged = { imported: 0, duplicates: 0, total, results: [] };
  let done = 0;
  for (let i = 0; i < total; i += BATCH) {
    const batch = files.slice(i, i + BATCH);
    const fd = new FormData();
    batch.forEach((f) => fd.append("files", f));
    onProgress?.({ done, total, pct: Math.round((done / total) * 100), current: batch.map((f) => f.name) });
    const res = await api.post("/payroll/imported-paystubs-bulk", fd, {
      onUploadProgress: (ev) => {
        if (!ev.total) return;
        const partial = (ev.loaded / ev.total) * batch.length * 0.8; // last 20% is the server scanning
        onProgress?.({ done, total, pct: Math.min(99, Math.round(((done + partial) / total) * 100)), current: batch.map((f) => f.name) });
      },
    });
    merged.imported += res.data.imported || 0;
    merged.duplicates += res.data.duplicates || 0;
    merged.results.push(...(res.data.results || []));
    done += batch.length;
    onProgress?.({ done, total, pct: Math.round((done / total) * 100), current: [] });
  }
  return merged;
};
